// ============================================
// BARRAINSTALAR.JS — el aviso discreto de "instala la app", UNA sola vez
// ============================================
// POR QUE EXISTE (INSTALAR-APP, 2026-09-14)
// La opcion de instalar vivia escondida en el menu del navegador: Ricardo no sabia que existia y su
// equipo de campo tampoco. Esta barra la saca a la superficie la PRIMERA vez que alguien entra
// desde el movil sin tenerla instalada.
//
// LO QUE NO PUEDE SER: una molestia diaria. Si la persona la cierra o instala, NO vuelve a
// aparecer nunca — la decision se guarda POR USUARIO (ver utils/instalacion.js), para que en un
// equipo compartido el "no, gracias" de uno no silencie el aviso al otro.
//
// Tampoco se enseña si la app ya corre instalada, ni en escritorio (ahi esta la entrada del menu).

import React, { useEffect, useState } from 'react';
import {
  debeMostrarAviso, marcarAvisoResuelto, estadoInstalacion,
  instalarNativo, suscribirInstalacion,
} from '../utils/instalacion';
import { X, Download } from 'lucide-react';

const PP = "'Poppins', sans-serif";

export default function BarraInstalar({ usuarioId, altoBarraInferior = 0 }) {
  const [visible, setVisible] = useState(() => debeMostrarAviso(usuarioId));
  const [estado, setEstado] = useState(estadoInstalacion);

  // Si el navegador tarda en disparar `beforeinstallprompt`, la barra aparece igual y el boton se
  // activa cuando llegue; en iOS nunca llega y el boton lleva a las instrucciones del menu.
  useEffect(() => suscribirInstalacion(() => setEstado(estadoInstalacion())), []);

  // Si la instalan desde otro sitio (el menu, o el propio navegador), la barra se va sola.
  useEffect(() => {
    if (estado === 'instalada' && visible) {
      marcarAvisoResuelto(usuarioId, 'instalada');
      setVisible(false);
    }
  }, [estado, visible, usuarioId]);

  if (!visible) return null;

  const cerrar = () => { marcarAvisoResuelto(usuarioId, 'cerrado'); setVisible(false); };

  const instalar = async () => {
    const r = await instalarNativo();
    if (r === 'aceptada') { marcarAvisoResuelto(usuarioId, 'instalada'); setVisible(false); return; }
    if (r === 'rechazada') { marcarAvisoResuelto(usuarioId, 'rechazada'); setVisible(false); return; }
    // 'sin-prompt' (iOS): no hay instalacion nativa que lanzar. Se manda al menu, donde estan los
    // pasos ilustrados, y NO se marca como resuelto: todavia no ha decidido nada.
    setVisible(false);
    window.dispatchEvent(new CustomEvent('beep:abrir-instrucciones-instalar'));
  };

  return (
    <div
      role="region"
      aria-label="Instalar Beep-OS"
      style={{
        position: 'fixed', left: 8, right: 8,
        // Por encima de la barra de navegacion inferior, mas el area segura del telefono.
        bottom: `calc(${altoBarraInferior + 8}px + env(safe-area-inset-bottom, 0px))`,
        zIndex: 9000,
        background: '#0f2b4a', color: '#fff',
        borderRadius: 14, padding: '10px 10px 10px 14px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
        display: 'flex', alignItems: 'center', gap: 10,
        fontFamily: PP,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.25 }}>
          Instala Beep-OS para entrar mas rapido
        </div>
        <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }}>
          Queda como una app mas, sin buscar en el navegador
        </div>
      </div>
      <button
        onClick={instalar}
        style={{
          flexShrink: 0, minHeight: 40, padding: '0 14px',
          background: '#b5d334', color: '#14301c', border: 'none', borderRadius: 10,
          fontFamily: PP, fontSize: 13, fontWeight: 800, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}
      >
        <Download size={15} strokeWidth={2.5} /> Instalar
      </button>
      <button
        onClick={cerrar}
        aria-label="Cerrar el aviso"
        style={{
          flexShrink: 0, width: 40, height: 40, background: 'transparent',
          border: 'none', color: 'rgba(255,255,255,0.65)', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <X size={18} strokeWidth={2.5} />
      </button>
    </div>
  );
}
